import { ApolloError } from '@apollo/client'
import { SnackbarVariant, useSnackbar } from '@focaldata/cin-ui-components'
import React, { useCallback } from 'react'
import { useAppDispatch, useAppSelector } from '../../../App.store'
import { LoggerErrorType } from '../../../data/logger'
import { QuestionSettingCode } from '../../../data/model/questionnaire'
import useGetDraftQuestionnaire from '../../../hooks/questionnaire/useGetDraftQuestionnaire'
import useHandleMultiSelectLimits from '../../../hooks/questionnaire/useHandleIntervalSettingsChange'
import { useUpdateQuestionnaireEntry } from '../../../hooks/questionnaire/useUpdateQuestionnaireEntry'
import { captureApolloError } from '../../../utils/HelperFunctions'
import {
  WithEntry,
  checkIfMatrixMultipleChoice,
  getQuestionSettings
} from '../../../utils/questionnaireUtils'
import MultiSelectChoiceLimit from '../BasicQuestion/QuestionCardFooter/MultiSelectChoiceLimit'
import { useCreateResponseOption } from '../BasicQuestion/ResponseOptions/ResponseOptions.hooks'
import { getAddScaleError } from '../Kpi/kpiErrorMessage'
import { entryHasKpi } from '../Kpi/kpiImpact'
import QuestionCardFooter from '../QuestionCardFooter'
import {
  responseOptionAdded,
  selectResponseOptionsByQuestion,
  selectSettingsByQuestionId
} from '../Questionnaire.slice'
import { getEntryId } from '../Questionnaire.utils'
import { scaleConfig } from '../constants'

const MatrixQuestion: React.FC<WithEntry> = (props: WithEntry) => {
  const { entry } = props
  const matrixTitleLk = getEntryId(entry)
  const dispatch = useAppDispatch()
  const createResponseOption = useCreateResponseOption(entry.id)

  const { updateQuestionnaireEntry } = useUpdateQuestionnaireEntry(entry.id)
  const { refetchQuestionnaire } = useGetDraftQuestionnaire()
  const { enqueueSnackbar } = useSnackbar()

  const responseOptionsByQuestion = useAppSelector(
    selectResponseOptionsByQuestion
  )

  const responseOptions = responseOptionsByQuestion[matrixTitleLk]

  const { handleIntervalSettingsChange, handleQuestionLimitChange } =
    useHandleMultiSelectLimits(entry)

  const handleAddScale = async () => {
    try {
      await updateQuestionnaireEntry({
        replaceResponseOptions: scaleConfig.Agree1To5
      })
    } catch (error) {
      captureApolloError(
        LoggerErrorType.ApolloMutation,
        'updateQuestionnaireEntryV2 - add scale',
        error as ApolloError
      )
      const { message, refetch } = getAddScaleError(error)
      enqueueSnackbar(message, { variant: SnackbarVariant.Error })
      if (refetch) {
        refetchQuestionnaire()
      }
    }
  }

  const handleChangeChoiceLimit: (settingValue: number) => void = useCallback(
    (settingValue) =>
      handleQuestionLimitChange(QuestionSettingCode.ChoiceLimit, settingValue),
    [handleQuestionLimitChange]
  )

  const settingValues =
    // @todo Legacy eslint violation – fix this when editing
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    useAppSelector((state) =>
      selectSettingsByQuestionId(state, matrixTitleLk)
    ) || []

  const isMultipleChoice = checkIfMatrixMultipleChoice(settingValues)
  const questionSettings = getQuestionSettings(settingValues)
  const choiceLimitValue = questionSettings.get(QuestionSettingCode.ChoiceLimit)
  const choiceLimit = choiceLimitValue && parseInt(choiceLimitValue, 10)

  const choiceIntervalMinValue = questionSettings.get(
    QuestionSettingCode.ChoiceIntervalMin
  )
  const choiceIntervalMin =
    choiceIntervalMinValue && parseInt(choiceIntervalMinValue, 10)

  const choiceIntervalMaxValue = questionSettings.get(
    QuestionSettingCode.ChoiceIntervalMax
  )
  const choiceIntervalMax =
    choiceIntervalMaxValue && parseInt(choiceIntervalMaxValue, 10)

  // @todo Legacy eslint violation – fix this when editing
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  const matrixResponseOptionsCount = responseOptions?.length

  return (
    <QuestionCardFooter
      entry={entry}
      onClickAddResponseOption={() => {
        dispatch(
          responseOptionAdded({
            questionLk: matrixTitleLk,
            position: responseOptions.length
          })
        )
        createResponseOption(responseOptions.length)
      }}
      addButtonText="Add response"
      showAddScale={!entryHasKpi(entry)}
      onAddScale={handleAddScale}
      multiSelectChoiceLimit={
        isMultipleChoice ? (
          <MultiSelectChoiceLimit
            entry={entry}
            responseOptionsCount={matrixResponseOptionsCount}
            choiceLimit={choiceLimit}
            choiceIntervalMin={choiceIntervalMin}
            choiceIntervalMax={choiceIntervalMax}
            onChangeChoiceLimit={handleChangeChoiceLimit}
            onChangeIntervalRange={handleIntervalSettingsChange}
          />
        ) : undefined
      }
    />
  )
}

export default MatrixQuestion
